// AI chat primitives (components.md "AI chat", JEV ui.component_recipe:
// core.chat.cited). The region is a column: a message list that scrolls on
// its own (role="log"), then the composer pinned to the region bottom on the
// same content line, separated by one structural hairline, never a fade.
// Assistant answers sit on layer-1 at the start edge, the user's questions
// on layer-2 at the end edge; numbers an answer relies on are listed under
// it in a cited-figures block with their source.
import { useEffect, useId, useLayoutEffect, useRef, type FormEvent, type ReactNode } from "react";
import { motion } from "motion/react";
import { ConsoleGlyph } from "./icons";
import { useEnterMotion } from "./motion";

export interface ChatPanelProps {
  /**
   * Visible heading (h2), e.g. "Asisten AI", when the chat is one region of a
   * page. Omit it on the AI Assistant page, whose PageHeader already names it.
   */
  title?: string;
  /** Accessible name when there is no title. Default "Asisten AI". */
  label?: string;
  /** One line under the heading: what the assistant can answer from. */
  description?: ReactNode;
  children: ReactNode;
}

/**
 * The chat region: a fixed-height column on a normal page, the rest of the
 * viewport inside <ConsoleShell fill>. Holds one ChatMessageList and one
 * ChatComposer, in that order.
 */
export function ChatPanel({ title, label = "Asisten AI", description, children }: ChatPanelProps) {
  const id = `${useId()}chat`;
  return (
    <section className="con-chat" aria-labelledby={title ? id : undefined} aria-label={title ? undefined : label}>
      {title ? (
        <div className="con-chat-head">
          <h2 className="con-panel-title" id={id}>
            {title}
          </h2>
          {description ? <p className="con-panel-description">{description}</p> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

export interface ChatMessageListProps {
  /** Accessible name of the log, e.g. "Percakapan". */
  label?: string;
  children: ReactNode;
}

/**
 * The scrolling log. When content is added and the reader is near the end,
 * it follows the newest message; a reader scrolled up stays where they are.
 */
export function ChatMessageList({ label = "Percakapan", children }: ChatMessageListProps) {
  const ref = useRef<HTMLDivElement>(null);
  const atEnd = useRef(true);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onScroll = () => {
      atEnd.current = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && atEnd.current) el.scrollTop = el.scrollHeight;
  });
  return (
    <div ref={ref} className="con-chat-log" role="log" aria-live="polite" aria-label={label} tabIndex={0}>
      <div className="con-chat-stack">{children}</div>
    </div>
  );
}

export type ChatRole = "user" | "assistant";

export interface ChatMessageProps {
  role: ChatRole;
  /** Speaker name for assistive tech (the article label). */
  speaker?: string;
  /** Time or status line under the bubble, e.g. "09.42". */
  meta?: string;
  /** A CitedFigures block, under an assistant answer only. */
  citations?: ReactNode;
  children: ReactNode;
}

export function ChatMessage({ role, speaker, meta, citations, children }: ChatMessageProps) {
  const enter = useEnterMotion({ rise: true });
  const who = speaker ?? (role === "user" ? "Anda" : "Asisten");
  return (
    <motion.article className="con-msg" data-role={role} aria-label={who} {...enter}>
      <div className="con-msg-bubble">{children}</div>
      {citations && role === "assistant" ? citations : null}
      {meta ? <p className="con-msg-meta">{meta}</p> : null}
    </motion.article>
  );
}

export interface CitedFigure {
  label: string;
  value: string;
  /** Where the number comes from, e.g. "Data pendaftar 2025 Ganjil". */
  source?: string;
}

export interface CitedFiguresProps {
  /** Heading text for the block. */
  title?: string;
  items: CitedFigure[];
}

/** The numbers an answer relies on, each with its source, under the answer. */
export function CitedFigures({ title = "Angka yang dirujuk", items }: CitedFiguresProps) {
  const id = `${useId()}cite`;
  return (
    <div className="con-cite" role="group" aria-labelledby={id}>
      <p className="con-cite-title" id={id}>
        {title}
      </p>
      <dl className="con-cite-list">
        {items.map((f) => (
          <div className="con-cite-row" key={`${f.label}${f.value}`}>
            <dt>{f.label}</dt>
            <dd className="con-cite-value">{f.value}</dd>
            {f.source ? <dd className="con-cite-source">{f.source}</dd> : null}
          </div>
        ))}
      </dl>
    </div>
  );
}

export interface ThinkingStateProps {
  /** What the assistant is doing, in words: "Menghitung konversi per prodi". */
  label: string;
}

/** The assistant's working state: a static-under-reduced-motion ring plus words. */
export function ThinkingState({ label }: ThinkingStateProps) {
  return (
    <div className="con-msg" data-role="assistant" role="status">
      <div className="con-msg-bubble con-thinking">
        <span className="jal-progress" data-shape="circular">
          <progress aria-label={label} />
        </span>
        <span>{label}</span>
      </div>
    </div>
  );
}

export interface SuggestionChipsProps {
  /** Toolbar name, e.g. "Pertanyaan yang disarankan". */
  label: string;
  suggestions: string[];
  /** One tap submits the suggestion. */
  onPick: (text: string) => void;
  disabled?: boolean;
}

export function SuggestionChips({ label, suggestions, onPick, disabled }: SuggestionChipsProps) {
  return (
    <div className="con-chips con-suggestions" role="toolbar" aria-label={label}>
      {suggestions.map((s) => (
        <button key={s} type="button" className="con-chip" data-kind="suggestion" title={s} disabled={disabled} onClick={() => onPick(s)}>
          <span>{s}</span>
        </button>
      ))}
    </div>
  );
}

export interface ChatComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (value: string) => void;
  /** Accessible name of the field; the placeholder is never the only label. */
  label?: string;
  placeholder?: string;
  /** While the assistant answers: the send button shows busy and input waits. */
  busy?: boolean;
  sendLabel?: string;
  /** Suggestion chips above the field. */
  suggestions?: ReactNode;
}

/**
 * Pinned to the bottom of its region. Enter sends, Shift+Enter breaks a line.
 * The field grows with its content (CSS field-sizing) up to five lines.
 */
export function ChatComposer({
  value,
  onChange,
  onSubmit,
  label = "Tanyakan sesuatu tentang data pendaftar",
  placeholder = "Tulis pertanyaan",
  busy = false,
  sendLabel = "Kirim pertanyaan",
  suggestions,
}: ChatComposerProps) {
  const send = (e?: FormEvent) => {
    e?.preventDefault();
    const text = value.trim();
    if (!text || busy) return;
    onSubmit(text);
  };
  return (
    <div className="con-composer">
      {suggestions}
      <form className="con-composer-row" onSubmit={send}>
        <textarea
          aria-label={label}
          rows={1}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) send(e);
          }}
        />
        <button type="submit" className="jal-icon-btn con-send" data-variant="filled" aria-label={sendLabel} aria-busy={busy || undefined} disabled={!value.trim() || busy}>
          <ConsoleGlyph name="send" size={20} />
        </button>
      </form>
    </div>
  );
}
